import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FaBuilding,
  FaGlobe,
  FaFileAlt,
  FaSyncAlt,
  FaExclamationTriangle,
  FaLayerGroup
} from 'react-icons/fa';
import {
  getDaysAgoIsoDate,
  getTodayIsoDate,
  MAX_HEATMAP_WINDOW_DAYS,
  validateHeatmapDateRange
} from '../shared/heatmapViewUtils';
import {
  loadAnalyticsSiteCollections,
  useAnalyticsOrganizations
} from '../../iapUsage/shared/analyticsViewUtils';
import './HeatmapFilterBar.css';

/**
 * Filter toolbar for Heatmap & Customer Analytics.
 * Handles organization, site collection, registered page, and 31-day date window selection.
 */
export default function HeatmapFilterBar({
  orgId,
  roles = [],
  pages = [],
  selectedPageId = '',
  selectedLayoutVersion = 1,
  onPageChange,
  onLayoutVersionChange,
  showPageSelector = false,
  allowAllPages = false,
  showDateFilter = true,
  onFilterChange,
  onRefresh,
  isLoading = false,
  disabled = false
}) {
  const today = getTodayIsoDate();

  // 1. Organizations (System Admin scope)
  const {
    isSystemAdmin,
    organizations,
    selectedOrg,
    setSelectedOrg,
    orgsLoading
  } = useAnalyticsOrganizations(roles, orgId);

  // 2. Site Collections
  const [siteCollections, setSiteCollections] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState(null);

  // 3. Date Inputs (Default 7-day window)
  const [startDate, setStartDate] = useState(() => getDaysAgoIsoDate(7));
  const [endDate, setEndDate] = useState(() => today);
  const [dateError, setDateError] = useState(null);
  const deviceClass = 'all';

  const lastEmittedRef = useRef(null);
  const fetchSitesAbortRef = useRef(null);

  const fetchSites = useCallback(async () => {
    if (fetchSitesAbortRef.current) {
      fetchSitesAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchSitesAbortRef.current = controller;

    setSelectedSite('');
    setSiteCollections([]);

    if (!selectedOrg) {
      setSitesLoading(false);
      return;
    }

    await loadAnalyticsSiteCollections({
      orgId: selectedOrg,
      setSitesLoading,
      setSitesError,
      setSiteCollections,
      setSelectedSite,
      signal: controller.signal
    });
  }, [selectedOrg]);

  useEffect(() => {
    fetchSites();
    return () => {
      if (fetchSitesAbortRef.current) {
        fetchSitesAbortRef.current.abort();
      }
    };
  }, [fetchSites]);

  // Date range validation check
  const checkDateValidation = useCallback((start, end) => {
    try {
      validateHeatmapDateRange(start, end);
      setDateError(null);
      return true;
    } catch (err) {
      setDateError(err.message);
      return false;
    }
  }, []);

  // Handle Date Change
  const handleDateChange = (type, val) => {
    let nextStart = startDate;
    let nextEnd = endDate;

    if (type === 'start') {
      nextStart = val;
      setStartDate(val);
    } else {
      nextEnd = val;
      setEndDate(val);
    }

    checkDateValidation(nextStart, nextEnd);
  };

  // Emit filter updates
  useEffect(() => {
    if (!selectedOrg || !startDate || !endDate) return;
    if (dateError) return;

    // Validate that selectedSite belongs to the currently loaded siteCollections
    const isSiteValid = siteCollections.some(
      (s) => String(s.id) === String(selectedSite)
    );
    const effectiveSite = isSiteValid ? selectedSite : '';

    const filterBundle = {
      organizationId: selectedOrg,
      siteCollectionId: effectiveSite,
      fromDate: startDate,
      toDate: endDate,
      pageId: selectedPageId,
      deviceClass
    };

    const serialized = JSON.stringify(filterBundle);
    if (lastEmittedRef.current === serialized) return;

    lastEmittedRef.current = serialized;
    if (typeof onFilterChange === 'function') {
      onFilterChange(filterBundle);
    }
  }, [selectedOrg, selectedSite, siteCollections, startDate, endDate, selectedPageId, dateError, onFilterChange]);

  return (
    <div className="heatmap-filter-bar" data-testid="heatmap-filter-bar">
      <div className="filter-group-row">
        {/* Organization Selector (System Admin only) */}
        {isSystemAdmin && (
          <div className="filter-item">
            <label htmlFor="heatmap-org-select">
              <FaBuilding className="filter-icon" /> Organization
            </label>
            <select
              id="heatmap-org-select"
              data-testid="heatmap-org-select"
              value={selectedOrg}
              onChange={(e) => {
                if (fetchSitesAbortRef.current) {
                  fetchSitesAbortRef.current.abort();
                }
                setSelectedOrg(e.target.value);
                setSelectedSite('');
                setSiteCollections([]);
              }}
              disabled={disabled || orgsLoading}
              className="filter-select"
            >
              {orgsLoading ? (
                <option value="">Loading organizations...</option>
              ) : (
                organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name || org.displayName || org.id}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        {/* Site Collection Selector */}
        <div className="filter-item">
          <label htmlFor="heatmap-site-select">
            <FaGlobe className="filter-icon" /> Site Collection
          </label>
          <select
            id="heatmap-site-select"
            data-testid="site-collection-select"
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            disabled={disabled || sitesLoading || siteCollections.length === 0}
            className="filter-select"
          >
            {sitesLoading ? (
              <option value="">Loading sites...</option>
            ) : siteCollections.length === 0 ? (
              <option value="">No sites available</option>
            ) : sitesError ? (
              <option value="">Error loading sites</option>
            ) : (
              siteCollections.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name || site.title || site.id}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Optional Page Selector (Heatmap Viewer) */}
        {showPageSelector && (
          <>
            <div className="filter-item">
              <label htmlFor="heatmap-page-select">
                <FaFileAlt className="filter-icon" /> Page
              </label>
              <select
                id="heatmap-page-select"
                data-testid="page-selector-select"
                value={selectedPageId}
                onChange={(e) => onPageChange && onPageChange(e.target.value)}
                disabled={disabled || (!allowAllPages && (!pages || pages.length === 0))}
                className="filter-select"
              >
                {allowAllPages && (
                  <option value="">All Pages (Site-wide)</option>
                )}
                {!pages || pages.length === 0 ? (
                  !allowAllPages && <option value="">No registered pages</option>
                ) : (
                  pages.map((p) => (
                    <option key={p.pageId} value={p.pageId}>
                      {p.name || p.pageId} (v{p.layoutVersion || 1}){p.snapshotNeeded === false ? ' ✓' : ''}
                    </option>
                  ))
                )}
              </select>
            </div>
            {selectedPageId && onLayoutVersionChange && (() => {
              const targetPage = (pages || []).find((p) => p.pageId === selectedPageId);
              const maxVersion = Math.max(1, Number(targetPage?.layoutVersion) || 1);
              const versionList = Array.from({ length: maxVersion }, (_, i) => i + 1);

              return (
                <div className="filter-item">
                  <label htmlFor="heatmap-layout-select">
                    <FaLayerGroup className="filter-icon" /> Version
                  </label>
                  <select
                    id="heatmap-layout-select"
                    data-testid="heatmap-layout-select"
                    value={selectedLayoutVersion || maxVersion}
                    onChange={(e) => onLayoutVersionChange(Number(e.target.value))}
                    disabled={disabled}
                    className="filter-select"
                  >
                    {versionList.map((ver) => (
                      <option key={ver} value={ver}>
                        v{ver} {ver === maxVersion ? '(Active)' : '(Archived)'}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}
          </>
        )}

        {/* Date Inputs */}
        {showDateFilter && (
          <>
            {/* Start Date */}
            <div className="filter-item date-input-item">
              <label htmlFor="heatmap-start-date">From</label>
              <input
                id="heatmap-start-date"
                type="date"
                value={startDate}
                max={endDate || today}
                onChange={(e) => handleDateChange('start', e.target.value)}
                disabled={disabled}
                className="filter-date-input"
              />
            </div>

            {/* End Date */}
            <div className="filter-item date-input-item">
              <label htmlFor="heatmap-end-date">To</label>
              <input
                id="heatmap-end-date"
                type="date"
                value={endDate}
                min={startDate}
                max={today}
                onChange={(e) => handleDateChange('end', e.target.value)}
                disabled={disabled}
                className="filter-date-input"
              />
            </div>
          </>
        )}

        {/* Refresh Action */}
        <div className="filter-actions-item">
          <button
            type="button"
            className="filter-refresh-btn"
            onClick={onRefresh}
            disabled={disabled || isLoading}
            title="Refresh analytics data"
          >
            <FaSyncAlt className={`refresh-icon ${isLoading ? 'spinning' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {showDateFilter && dateError && (
        <div className="filter-warning-banner" role="alert">
          <FaExclamationTriangle className="warning-icon" />
          <span>{dateError} (Maximum allowed window is {MAX_HEATMAP_WINDOW_DAYS} days).</span>
        </div>
      )}
    </div>
  );
}

HeatmapFilterBar.propTypes = {
  orgId: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  pages: PropTypes.arrayOf(PropTypes.shape({
    pageId: PropTypes.string.isRequired,
    name: PropTypes.string,
    layoutVersion: PropTypes.number
  })),
  selectedPageId: PropTypes.string,
  onPageChange: PropTypes.func,
  showPageSelector: PropTypes.bool,
  allowAllPages: PropTypes.bool,
  showDateFilter: PropTypes.bool,
  onFilterChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool
};
